import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Toaster, toast } from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faAngleLeft, faAngleRight, faSpinner, faEye, faDownload, faExclamationTriangle, faBan, faInfoCircle, faXmark, faSync, faUpload, faTimes } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import axios from 'axios';
import { BASE_URL } from '../../config';
import CancelSeriesModalCardPrintingFee from './CancelSeriesModalCardPrintingFee';
import ViewCancellationModalCPF from './ViewCancellationModalCPF';
import * as XLSX from 'xlsx';

const TEMPLATE_URL = 'https://public-testing.ap-south-1.linodeobjects.com/delivery-date-template.xlsx';

const CardPrintingFeeHistory = () => {
  // State for filters
  const [filters, setFilters] = useState({
    billingType: 'SI',
    seriesNo: '',
    dateFrom: '',
    dateTo: '',
    corporate: [],
    errorStatus: 'all',
    cancelStatus: 'all',
  });

  // State for corporate search
  const [corporateSearch, setCorporateSearch] = useState('');
  const [filteredCorporations, setFilteredCorporations] = useState([]);

  // State for table
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [seriesData, setSeriesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fileLoading, setFileLoading] = useState(null);

  // State for errors modal
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorSeriesNoUid, setErrorSeriesNoUid] = useState(null);
  const [errorData, setErrorData] = useState([]);
  const [errorPage, setErrorPage] = useState(1);
  const [errorPerPage, setErrorPerPage] = useState(10);
  const [errorTotalPages, setErrorTotalPages] = useState(1);
  const [errorTotalEntries, setErrorTotalEntries] = useState(0);
  const [errorLoading, setErrorLoading] = useState(false);

  // State for cancel modal
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelSeriesNo, setCancelSeriesNo] = useState(null);
  const [cancelSeriesNoUid, setCancelSeriesNoUid] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);

  // State for view cancellation modal
  const [viewCancelModalOpen, setViewCancelModalOpen] = useState(false);
  const [cancellationDetails, setCancellationDetails] = useState(null);
  const [viewCancelSeriesNo, setViewCancelSeriesNo] = useState(null);
  const [viewCancelLoading, setViewCancelLoading] = useState(false);

  // State for row selection and batch upload
  const [selectedRows, setSelectedRows] = useState([]);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [successfulUploads, setSuccessfulUploads] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const allowedFileTypes = ['xlsx', 'xls'];
  const maxFileSizeMB = 20;
  const maxUploadLimit = 5;

  // Formatter for currency
  const phpFormatter = new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // Handle row selection
  const handleRowSelect = (series) => {
    setSelectedRows((prev) => {
      if (prev.some((row) => row.series_no_uid === series.series_no_uid)) {
        return prev.filter((row) => row.series_no_uid !== series.series_no_uid);
      }
      return [...prev, series];
    });
  };

  // Handle select all rows
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelections = seriesData.filter(
        (series) => !selectedRows.some((row) => row.series_no_uid === series.series_no_uid)
      );
      setSelectedRows((prev) => [...prev, ...newSelections]);
    } else {
      const currentPageIds = seriesData.map((series) => series.series_no_uid);
      setSelectedRows((prev) => prev.filter((row) => !currentPageIds.includes(row.series_no_uid)));
    }
  };

  // Download selected series template
  const handleDownloadTemplate = () => {
    if (selectedRows.length === 0) {
      toast.error('Please select at least one series to download.', { duration: 3000 });
      return;
    }

    const data = selectedRows.map((row) => ({
      'Series Unique ID': row.series_no_uid,
      'Series No': row.series_no,
      'Date Delivered': '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Selected Series');
    
    worksheet['!cols'] = [
      { wch: 36 },
      { wch: 15 },
      { wch: 15 },
    ];

    XLSX.writeFile(workbook, `Selected_Series_Template_${new Date().toISOString().replace(/[:.]/g, '-')}.xlsx`);
    toast.success('Template downloaded successfully.', { duration: 3000 });
  };

  // Open batch upload modal
  const handleBatchUpload = () => {
    setUploadModalOpen(true);
    setFiles([]);
    setSuccessfulUploads([]);
    setIsDragging(false);
  };

  // Handle file selection for upload
  const handleFileChange = (selectedFiles) => {
    const validFiles = Array.from(selectedFiles).filter((file) => {
      const fileExtension = file.name.split('.').pop().toLowerCase();
      const isAllowedType = allowedFileTypes.includes(fileExtension);
      const isWithinSizeLimit = file.size <= maxFileSizeMB * 1024 * 1024;

      if (!isAllowedType) {
        toast.error(
          `File ${file.name} is not supported. Only ${allowedFileTypes.join(', ')} files allowed.`,
          { duration: 3000 }
        );
        return false;
      }

      if (!isWithinSizeLimit) {
        toast.error(
          `File ${file.name} exceeds ${maxFileSizeMB}MB size limit.`,
          { duration: 3000 }
        );
        return false;
      }

      return true;
    });

    if (files.length + validFiles.length > maxUploadLimit) {
      toast.error(
        `You can only upload up to ${maxUploadLimit} files at a time.`,
        { duration: 3000 }
      );
      return;
    }

    const newFiles = validFiles.map((file) => ({
      file,
      progress: 0,
      isUploading: false,
      isPolling: false,
      uploadId: null,
      error: null,
      status: null,
      summary: null,
    }));

    setFiles((prevFiles) => [...prevFiles, ...newFiles]);
    newFiles.forEach((fileObj) => handleFileUpload(fileObj));
  };

  // Handle file upload
  const handleFileUpload = async (fileObj) => {
    setFiles((prevFiles) =>
      prevFiles.map((f) =>
        f.file === fileObj.file ? { ...f, isUploading: true } : f
      )
    );
    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', fileObj.file);

      const response = await axios.post(`${BASE_URL}/card-printing-fee/file-upload/delivery-date`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setFiles((prevFiles) =>
            prevFiles.map((f) =>
              f.file === fileObj.file ? { ...f, progress: percentCompleted } : f
            )
          );
        },
      });

      const uploadId = response.data.upload_id;
      setFiles((prevFiles) =>
        prevFiles.map((f) =>
          f.file === fileObj.file
            ? { ...f, isUploading: false, isPolling: true, progress: 100, uploadId }
            : f
        )
      );
      setSuccessfulUploads((prev) => [...prev, uploadId]);
      toast.success(`File ${fileObj.file.name} uploaded successfully!`, { duration: 3000 });

      let attempts = 0;
      const maxAttempts = 60;
      const pollInterval = 3000;

      const pollStatus = async () => {
        try {
          const statusResponse = await axios.get(`${BASE_URL}/card-printing-fee/file-upload/delivery-upload-status`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
            },
            params: { upload_id: uploadId },
          });

          const upload = statusResponse.data.data;
          if (upload.status === 'completed' || upload.status === 'failed') {
            setFiles((prevFiles) =>
              prevFiles.map((f) =>
                f.file === fileObj.file
                  ? {
                      ...f,
                      isPolling: false,
                      status: upload.status,
                      summary: {
                        successCount: upload.success_update_count || 0,
                        failedCount: upload.failed_update_count || 0,
                        errorMessage: upload.error_message || null,
                      },
                    }
                  : f
              )
            );
            if (upload.status === 'completed') {
              toast.success(`File ${fileObj.file.name} processed successfully!`, { duration: 3000 });
              fetchSeriesHistory();
            } else {
              toast.error(`File ${fileObj.file.name} processing failed: ${upload.error_message || 'Unknown error'}`, { duration: 3000 });
            }
          } else if (attempts < maxAttempts) {
            attempts++;
            setTimeout(pollStatus, pollInterval);
          } else {
            setFiles((prevFiles) =>
              prevFiles.map((f) =>
                f.file === fileObj.file
                  ? {
                      ...f,
                      isPolling: false,
                      status: 'timeout',
                      summary: { errorMessage: 'Upload processing timed out.' },
                    }
                  : f
              )
            );
            toast.error(`File ${fileObj.file.name} processing timed out.`, { duration: 3000 });
          }
        } catch (error) {
          if (attempts < maxAttempts) {
            attempts++;
            setTimeout(pollStatus, pollInterval);
          } else {
            setFiles((prevFiles) =>
              prevFiles.map((f) =>
                f.file === fileObj.file
                  ? {
                      ...f,
                      isPolling: false,
                      status: 'error',
                      summary: { errorMessage: 'Error checking upload status.' },
                    }
                  : f
              )
            );
            toast.error(`Error checking status for ${fileObj.file.name}.`, { duration: 3000 });
          }
        }
      };

      setTimeout(pollStatus, pollInterval);
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to upload file.';
      setFiles((prevFiles) =>
        prevFiles.map((f) =>
          f.file === fileObj.file
            ? { ...f, isUploading: false, isPolling: false, error: errorMessage }
            : f
        )
      );
      toast.error(errorMessage, { duration: 3000 });
    } finally {
      setIsUploading(false);
    }
  };

  // Handle drag and drop events
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = e.dataTransfer.files;
    handleFileChange(droppedFiles);
  };

  // Remove uploaded file
  const handleRemoveFile = async (fileObj) => {
    const result = await Swal.fire({
      title: 'Remove File?',
      text: `Are you sure you want to remove ${fileObj.file.name}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#4f46e5',
      cancelButtonColor: '#ef4444',
      confirmButtonText: 'Yes, remove it',
    });

    if (result.isConfirmed) {
      setFiles((prevFiles) => prevFiles.filter((f) => f.file !== fileObj.file));
      if (fileObj.uploadId) {
        setSuccessfulUploads((prev) =>
          prev.filter((id) => id !== fileObj.uploadId)
        );
      }
      toast.success('File removed successfully.', { duration: 3000 });
    }
  };

  // Trigger file input
  const handleChooseFile = () => {
    fileInputRef.current.click();
  };

  // Fetch corporations
  const fetchCorporations = async (search = '') => {
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}/card-printing-fee/corporations`, {
        params: { search, billing_type: filters.billingType || undefined },
        headers: { Authorization: `Bearer ${localStorage.getItem('authentication/token')}` },
      });
      setFilteredCorporations(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch corporations.', { duration: 3000 });
    } finally {
      setLoading(false);
    }
  };

  // Handle corporate search
  const handleCorporateSearch = (e) => {
    const value = e.target.value;
    setCorporateSearch(value);
    fetchCorporations(value);
  };

  // Handle corporate selection
  const handleCorporateSelect = (corp) => {
    if (!filters.corporate.some((c) => c.id === corp.id)) {
      setFilters((prev) => ({
        ...prev,
        corporate: [...prev.corporate, corp],
      }));
      setCorporateSearch('');
      setFilteredCorporations([]);
    }
  };

  // Remove corporate filter
  const handleRemoveCorporate = (corpId) => {
    setFilters((prev) => ({
      ...prev,
      corporate: prev.corporate.filter((c) => c.id !== corpId),
    }));
  };

  // Fetch series history
  const fetchSeriesHistory = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}/card-printing-fee/history`, {
        params: {
          billing_type: filters.billingType || undefined,
          series_no: filters.seriesNo || undefined,
          date_from: filters.dateFrom || undefined,
          date_to: filters.dateTo || undefined,
          corporate: filters.corporate.map((c) => c.id),
          error_status: filters.errorStatus !== 'all' ? filters.errorStatus : undefined,
          cancel_status: filters.cancelStatus !== 'all' ? filters.cancelStatus : undefined,
          page: currentPage,
          per_page: itemsPerPage,
        },
        headers: { Authorization: `Bearer ${localStorage.getItem('authentication/token')}` },
      });

      const seriesWithErrors = await Promise.all(
        response.data.data.map(async (series) => {
          const errorCountResponse = await axios.get(`${BASE_URL}/card-printing-fee/series/${series.series_no_uid}/errors`, {
            params: { per_page: 1 },
            headers: { Authorization: `Bearer ${localStorage.getItem('authentication/token')}` },
          });
          return {
            ...series,
            error_count: errorCountResponse.data.pagination.total_records,
          };
        })
      );

      setSeriesData(seriesWithErrors);
      setTotalEntries(response.data.pagination.total_records);
      setTotalPages(response.data.pagination.total_pages);
    } catch (error) {
      toast.error('Failed to fetch series history.', { duration: 3000 });
    } finally {
      setLoading(false);
    }
  };

  // Fetch errors
  const fetchSeriesErrors = async (seriesNoUid) => {
    try {
      setErrorLoading(true);
      const response = await axios.get(`${BASE_URL}/card-printing-fee/series/${seriesNoUid}/errors`, {
        params: { page: errorPage, per_page: errorPerPage },
        headers: { Authorization: `Bearer ${localStorage.getItem('authentication/token')}` },
      });
      setErrorData(response.data.data);
      setErrorTotalEntries(response.data.pagination.total_records);
      setErrorTotalPages(response.data.pagination.total_pages);
    } catch (error) {
      toast.error('Failed to fetch series errors.', { duration: 3000 });
    } finally {
      setErrorLoading(false);
    }
  };

  // Open errors modal
  const openErrorModal = (seriesNoUid) => {
    setErrorSeriesNoUid(seriesNoUid);
    setErrorPage(1);
    setErrorModalOpen(true);
    fetchSeriesErrors(seriesNoUid);
  };

  // Handle file download
  const handleDownloadFiles = async (seriesNoUid, seriesNo) => {
    try {
      setFileLoading(seriesNoUid);
      const response = await axios.get(`${BASE_URL}/card-printing-fee/series/${seriesNoUid}/pdfs`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${localStorage.getItem('authentication/token')}` },
      });

      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `CardPrintingFee_Series_${seriesNo}_${new Date().toISOString().replace(/[:.]/g, '-')}.zip`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Files downloaded successfully.', { duration: 3000 });
    } catch (error) {
      toast.error('Failed to download files.', { duration: 3000 });
    } finally {
      setFileLoading(null);
    }
  };

  // Handle cancel series
  const handleCancelSeries = async (seriesNoUid, reason) => {
    const result = await Swal.fire({
      title: `Cancel Series ${cancelSeriesNo}?`,
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, cancel it!',
    });

    if (!result.isConfirmed) return;

    try {
      setCancelLoading(true);
      await axios.post(
        `${BASE_URL}/card-printing-fee/series/${seriesNoUid}/cancel`,
        { reason },
        { headers: { Authorization: `Bearer ${localStorage.getItem('authentication/token')}` } }
      );
      toast.success('Series cancelled successfully.', { duration: 3000 });
      fetchSeriesHistory();
      setCancelModalOpen(false);
    } catch (error) {
      toast.error('Failed to cancel series.', { duration: 3000 });
    } finally {
      setCancelLoading(false);
    }
  };

  // Handle view cancellation
  const handleViewCancellation = async (seriesNo, seriesNoUid) => {
    try {
      setViewCancelLoading(true);
      const response = await axios.get(
        `${BASE_URL}/card-printing-fee/series/${seriesNoUid}/cancellation-details`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('authentication/token')}` } }
      );
      setCancellationDetails(response.data.data);
      setViewCancelSeriesNo(seriesNo);
      setViewCancelModalOpen(true);
    } catch (error) {
      toast.error('Failed to fetch cancellation details.', { duration: 3000 });
    } finally {
      setViewCancelLoading(false);
    }
  };

  // Open cancel modal
  const openCancelModal = (seriesNo, seriesNoUid) => {
    setCancelSeriesNo(seriesNo);
    setCancelSeriesNoUid(seriesNoUid);
    setCancelModalOpen(true);
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  // Fetch data
  useEffect(() => {
    fetchSeriesHistory();
  }, [filters, currentPage, itemsPerPage]);

  // Fetch errors
  useEffect(() => {
    if (errorModalOpen && errorSeriesNoUid) {
      fetchSeriesErrors(errorSeriesNoUid);
    }
  }, [errorPage, errorPerPage]);

  // Table columns
  const columns = [
    { key: 'select', label: <input type="checkbox" onChange={handleSelectAll} checked={seriesData.every((series) => selectedRows.some((row) => row.series_no_uid === series.series_no_uid))} />, sortable: false },
    { key: 'series_no', label: 'Series No.' },
    { key: 'member_count', label: 'Members' },
    { key: "corp_names", label: "Corporate Name", sortable: true },
    { key: 'billing_type', label: 'Type' },
    // { key: 'total_vat_amount', label: 'VAT' },
    // { key: 'total_net_amount', label: 'Net' },
    // { key: 'total_gross_amount', label: 'Gross' },
    {
      key: 'total_net_amount',
      label: (
        <span className="inline-flex items-center">
          <span className="bg-blue-100 text-blue-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded-full">
            NET
          </span>
          Total Net
        </span>
      ),
      sortable: true,
    },
    {
      key: 'total_vat_amount',
      label: (
        <span className="inline-flex items-center">
          <span className="bg-green-100 text-green-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded-full">
            VAT
          </span>
          Total VAT
        </span>
      ),
      sortable: true,
    },
    {
      key: 'total_gross_amount',
      label: (
        <span className="inline-flex items-center">
          <span className="bg-purple-100 text-purple-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded-full">
            GROSS
          </span>
          Total Gross
        </span>
      ),
      sortable: true,
    },
    { key: 'date_created', label: 'Created' },
    { key: 'delivery_date', label: 'Delivered' },
    { key: 'actions', label: 'Actions' },
  ];

  // Error table columns
  const errorColumns = [
    { key: 'series_no', label: 'Series No.' },
    { key: 'corp_code', label: 'Corp Code' },
    { key: 'member_id', label: 'Member ID' },
    { key: 'member_name', label: 'Name' },
    { key: 'member_type', label: 'Type' },
    { key: 'error_message', label: 'Error' },
    { key: 'failed_at', label: 'Failed At' },
  ];

  // Pagination
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const handleErrorPageChange = (page) => {
    if (page >= 1 && page <= errorTotalPages) setErrorPage(page);
  };

  const handleErrorItemsPerPageChange = (value) => {
    setErrorPerPage(value);
    setErrorPage(1);
  };

  const getShowingEntriesText = () => {
    const start = (currentPage - 1) * itemsPerPage + 1;
    const end = Math.min(currentPage * itemsPerPage, totalEntries);
    return `Showing ${start} to ${end} of ${totalEntries.toLocaleString()} series`;
  };

  const getErrorShowingEntriesText = () => {
    const start = (errorPage - 1) * errorPerPage + 1;
    const end = Math.min(errorPage * errorPerPage, errorTotalEntries);
    return `Showing ${start} to ${end} of ${errorTotalEntries.toLocaleString()} errors`;
  };

  // Loading Skeleton
  const LoadingSkeleton = ({ rows = 5, columns = 8 }) => (
    <>
      {Array(rows).fill().map((_, i) => (
        <tr key={i} className="animate-pulse border-b border-gray-100">
          {Array(columns).fill().map((_, j) => (
            <td key={j} className="p-2">
              <div className="h-3 bg-gray-200 rounded w-full"></div>
            </td>
          ))}
        </tr>
      ))}
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4 font-sans">
      <Toaster position="top-right" toastOptions={{ duration: 3000 }} />
      <div className="max-w-1xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-indigo-600 text-white rounded-lg">
            <FontAwesomeIcon icon={faSearch} className="text-sm" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Card Printing Fee (Billed)</h1>
            <p className="text-xs text-gray-500">View and manage billing series</p>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {/* Filters */}
          <div className="p-4 bg-gray-50 border-b border-gray-100">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">

               <div className="relative">
                <label className="block text-xs font-medium text-gray-700 mb-1">Corporate</label>
                <input
                  type="text"
                  placeholder="Search corporate..."
                  value={corporateSearch}
                  onChange={handleCorporateSearch}
                  className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500"
                />
                {filteredCorporations.length > 0 && (
                  <ul className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg mt-1 max-h-40 overflow-y-auto shadow-lg">
                    {filteredCorporations.map((corp) => (
                      <li
                        key={corp.id}
                        onClick={() => handleCorporateSelect(corp)}
                        className="px-3 py-1.5 text-sm hover:bg-indigo-50 cursor-pointer"
                      >
                        {corp.id} - {corp.name}
                      </li>
                    ))}
                  </ul>
                )}
                {filters.corporate.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {filters.corporate.map((corp) => (
                      <span
                        key={corp.id}
                        className="inline-flex items-center px-2 py-0.5 text-xs bg-indigo-100 text-indigo-800 rounded-full"
                      >
                        {corp.name}
                        <button
                          onClick={() => handleRemoveCorporate(corp.id)}
                          className="ml-1 text-indigo-600 hover:text-indigo-800"
                        >
                          <FontAwesomeIcon icon={faXmark} className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>


              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Billing Type</label>
                <select
                  value={filters.billingType}
                  onChange={(e) => handleFilterChange('billingType', e.target.value)}
                  className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500"
                >
                  <option value="SI">(SI) Sales Invoice </option>
                  <option value="SOA">(SOA) Statement of Account</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Series Number</label>
                <input
                  type="number"
                  value={filters.seriesNo}
                  onChange={(e) => handleFilterChange('seriesNo', e.target.value)}
                  className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500"
                  placeholder="Enter series number"
                  min="1"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Date Created At - From:</label>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Date Created At - To:</label>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Error Status</label>
                <select
                  value={filters.errorStatus}
                  onChange={(e) => handleFilterChange('errorStatus', e.target.value)}
                  className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500"
                >
                  <option value="all">All</option>
                  <option value="without_errors">No Errors</option>
                  <option value="with_errors">With Errors</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Cancel Status</label>
                <select
                  value={filters.cancelStatus}
                  onChange={(e) => handleFilterChange('cancelStatus', e.target.value)}
                  className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500"
                >
                  <option value="all">All</option>
                  <option value="not_cancelled">Not Cancelled</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

             
            </div>
          </div>

          {/* Table */}
          <div className="p-4">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-lg font-semibold text-gray-800">Card Printing Fee Billing Series</h2>
              <button
                onClick={() => fetchSeriesHistory()}
                className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-1 text-xs"
                disabled={loading}
              >
                <FontAwesomeIcon icon={loading ? faSpinner : faSync} spin={loading} />
                Refresh
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-100">
                  <tr>
                    {columns.map((column) => (
                      <th key={column.key} className="p-2 text-left font-semibold text-gray-600">
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loading ? (
                    <LoadingSkeleton rows={itemsPerPage} columns={columns.length} />
                  ) : seriesData.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length} className="p-2 text-center text-gray-500">
                        No series found.
                      </td>
                    </tr>
                  ) : (
                    seriesData.map((series) => (
                      <tr
                        key={series.series_no_uid}
                        className={`hover:bg-gray-50 transition ${series.error_count > 0 ? 'bg-red-50' : ''} ${!series.active ? 'bg-gray-100' : ''}`}
                      >
                        <td className="p-2">
                          <input
                            type="checkbox"
                            checked={selectedRows.some((row) => row.series_no_uid === series.series_no_uid)}
                            onChange={() => handleRowSelect(series)}
                          />
                        </td>
                        {/* <td className="p-2">
                          {series.series_no}
                          {!series.active && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              CANCELLED
                            </span>
                          )}
                        </td> */}
                        
                        <td className="p-3 text-sm">
                          {filters.billingType == 'SOA' ? (
                            <>AR - </>
                          ) : null}
                          {series.series_no}
                          {!series.active ? (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              CANCELLED
                            </span>
                          ) : null}
                        </td>

                        <td className="p-2">{series.member_count}</td>
                        <td className="p-2">{series.corp_names}</td>
                        <td className="p-2">{series.billing_type}</td>
                        <td className="p-2">{phpFormatter.format(series.total_net_amount)}</td>
                        <td className="p-2">{phpFormatter.format(series.total_vat_amount)}</td>
                        <td className="p-2">{phpFormatter.format(series.total_gross_amount)}</td>
                        <td className="p-2">{series.date_created}</td>
                        <td className="p-2">
                          {series.delivery_date ? (
                            <span className="text-green-600">{series.delivery_date}</span>
                          ) : (
                            <span className="text-red-600">Not Yet Delivered</span>
                          )}
                        </td>
                        <td className="p-2 flex gap-2">
                          <Link
                            to={`/billing/card-printing-fee-billed/${series.series_no_uid}?series=${series.series_no}`}
                            className="text-indigo-600 hover:text-indigo-800"
                          >
                            <FontAwesomeIcon icon={faEye} className="h-4 w-4" />
                          </Link>
                          <button
                            onClick={() => handleDownloadFiles(series.series_no_uid, series.series_no)}
                            className="text-green-600 hover:text-green-800"
                            disabled={fileLoading === series.series_no_uid}
                          >
                            <FontAwesomeIcon icon={fileLoading === series.series_no_uid ? faSpinner : faDownload} spin={fileLoading === series.series_no_uid} className="h-4 w-4" />
                          </button>
                          {series.error_count > 0 && (
                            <button
                              onClick={() => openErrorModal(series.series_no_uid)}
                              className="text-yellow-600 hover:text-yellow-800"
                            >
                              <FontAwesomeIcon icon={faExclamationTriangle} className="h-4 w-4" />
                            </button>
                          )}
                          {series.active ? (
                            <button
                              onClick={() => openCancelModal(series.series_no, series.series_no_uid)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <FontAwesomeIcon icon={faBan} className="h-4 w-4" />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleViewCancellation(series.series_no, series.series_no_uid)}
                              className="text-gray-600 hover:text-gray-800"
                            >
                              <FontAwesomeIcon icon={faInfoCircle} className="h-4 w-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination and Actions */}
          <div className="p-4 border-t border-gray-100">
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={handleDownloadTemplate}
                  className="px-3 py-1.5 text-xs text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 flex items-center gap-1"
                >
                  <FontAwesomeIcon icon={faDownload} className="h-4 w-4" />
                  Download Selected Series
                </button>
                <button
                  onClick={handleBatchUpload}
                  className="px-3 py-1.5 text-xs text-white bg-gray-600 rounded-lg hover:bg-gray-700 flex items-center gap-1"
                >
                  <FontAwesomeIcon icon={faUpload} className="h-4 w-4" />
                  Batch Upload Delivery Date
                </button>
              </div>
              <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <select
                    value={itemsPerPage}
                    onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                    className="px-2 py-1 border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500"
                  >
                    {[5, 10, 20, 50, 100].map((val) => (
                      <option key={val} value={val}>{val}/page</option>
                    ))}
                  </select>
                  <span>{getShowingEntriesText()}</span>
                  <span>Selected: {selectedRows.length} series</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1 || loading}
                    className="p-1.5 text-gray-600 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                  >
                    <FontAwesomeIcon icon={faAngleLeft} />
                  </button>
                  <span className="text-xs text-gray-600">{currentPage} of {totalPages}</span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages || loading}
                    className="p-1.5 text-gray-600 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                  >
                    <FontAwesomeIcon icon={faAngleRight} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Errors Modal */}
        {errorModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-4 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-3">
                <h2 className="text-lg font-semibold text-gray-800">Series Errors</h2>
                <button
                  onClick={() => setErrorModalOpen(false)}
                  className="text-gray-600 hover:text-gray-800"
                >
                  <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-100">
                    <tr>
                      {errorColumns.map((column) => (
                        <th key={column.key} className="p-2 text-left font-semibold text-gray-600">
                          {column.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {errorLoading ? (
                      <LoadingSkeleton rows={errorPerPage} columns={errorColumns.length} />
                    ) : errorData.length === 0 ? (
                      <tr>
                        <td colSpan={errorColumns.length} className="p-2 text-center text-gray-500">
                          No errors found.
                        </td>
                      </tr>
                    ) : (
                      errorData.map((error) => (
                        <tr key={error.id} className="hover:bg-gray-50 transition">
                          <td className="p-2">{error.series_no}</td>
                          <td className="p-2">{error.corp_code}</td>
                          <td className="p-2">{error.member_id}</td>
                          <td className="p-2">{error.member_name}</td>
                          <td className="p-2">{error.member_type === 'P' ? 'Principal' : 'Dependent'}</td>
                          <td className="p-2 bg-red-50">{error.error_message}</td>
                          <td className="p-2">{error.failed_at}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 flex flex-col sm:flex-row justify-between items-center gap-3">
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <select
                    value={errorPerPage}
                    onChange={(e) => handleErrorItemsPerPageChange(Number(e.target.value))}
                    className="px-2 py-1 border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500"
                  >
                    {[5, 10, 20, 50].map((val) => (
                      <option key={val} value={val}>{val}/page</option>
                    ))}
                  </select>
                  <span>{getErrorShowingEntriesText()}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleErrorPageChange(errorPage - 1)}
                    disabled={errorPage === 1 || errorLoading}
                    className="p-1.5 text-gray-600 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                  >
                    <FontAwesomeIcon icon={faAngleLeft} />
                  </button>
                  <span className="text-xs text-gray-600">{errorPage} of {errorTotalPages}</span>
                  <button
                    onClick={() => handleErrorPageChange(errorPage + 1)}
                    disabled={errorPage === errorTotalPages || errorLoading}
                    className="p-1.5 text-gray-600 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                  >
                    <FontAwesomeIcon icon={faAngleRight} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Cancel Series Modal */}
        <CancelSeriesModalCardPrintingFee
          isOpen={cancelModalOpen}
          onClose={() => setCancelModalOpen(false)}
          onConfirm={handleCancelSeries}
          seriesNo={cancelSeriesNo}
          seriesNoUid={cancelSeriesNoUid}
          loading={cancelLoading}
        />

        {/* View Cancellation Modal */}
        <ViewCancellationModalCPF
          isOpen={viewCancelModalOpen}
          onClose={() => setViewCancelModalOpen(false)}
          cancellationDetails={cancellationDetails}
          seriesNo={viewCancelSeriesNo}
        />

        {/* Loading Indicator */}
        {(fileLoading || viewCancelLoading) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-4 flex flex-col items-center gap-2">
              <FontAwesomeIcon icon={faSpinner} className="text-indigo-600 text-xl animate-spin" />
              <p className="text-xs text-gray-800">{viewCancelLoading ? 'Fetching cancellation details...' : 'Generating files...'}</p>
            </div>
          </div>
        )}

        {/* Upload Delivery Dates Modal */}
        {uploadModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-4 w-full max-w-lg">
              <div className="flex justify-between items-center mb-3">
                <h2 className="text-lg font-semibold text-gray-800">Upload Delivery Dates</h2>
                <button
                  onClick={() => setUploadModalOpen(false)}
                  className="text-gray-600 hover:text-gray-800"
                >
                  <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
                </button>
              </div>
              <div className="flex flex-col gap-3">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <h3 className="text-xs font-semibold text-gray-800 mb-1">Excel File Format Instructions</h3>
                  <p className="text-xs text-gray-600 mb-1">
                    Ensure your Excel file follows the template format with these columns:
                  </p>
                  <ul className="list-disc list-inside text-xs text-gray-600 mb-1">
                    <li><strong>Series Unique ID</strong>: Unique identifier for the series.</li>
                    <li><strong>Series No</strong>: Series number.</li>
                    <li><strong>Date Delivered</strong>: Delivery date in <code>YYYY-MM-DD</code> format (e.g., 2025-12-31).</li>
                  </ul>
                  <a
                    href={TEMPLATE_URL}
                    download="card-printing-fee-delivery-template.xlsx"
                    className="inline-flex items-center px-2 py-1 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-xs"
                  >
                    <FontAwesomeIcon icon={faDownload} className="mr-1 h-3 w-3" />
                    Download Template
                  </a>
                </div>
                <div
                  className={`relative border-2 border-dashed rounded-lg p-4 text-center transition-all ${
                    isDragging ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 bg-gray-50'
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <FontAwesomeIcon icon={faUpload} className="text-2xl text-indigo-500 mb-1" />
                  <p className="text-xs text-gray-700">
                    Drag and drop Excel files here or{' '}
                    <button
                      type="button"
                      onClick={handleChooseFile}
                      className="text-indigo-600 font-semibold hover:text-indigo-800"
                    >
                      choose files
                    </button>
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Supported formats: .xlsx, .xls (Max {maxFileSizeMB}MB per file, up to {maxUploadLimit} files)
                  </p>
                  <input
                    type="file"
                    ref={fileInputRef}
                    multiple
                    accept=".xlsx,.xls"
                    onChange={(e) => handleFileChange(e.target.files)}
                    className="hidden"
                  />
                </div>
                {files.length > 0 && (
                  <div className="mt-3">
                    <h3 className="text-xs font-semibold text-gray-800 mb-1">Selected Files</h3>
                    <div className="flex justify-end gap-2 mb-3">
                      <button
                        onClick={() => setUploadModalOpen(false)}
                        className="px-3 py-1.5 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 text-xs"
                        disabled={isUploading}
                      >
                        Close
                      </button>
                    </div>
                    <ul className="space-y-2">
                      {files.map((fileObj, index) => (
                        <li
                          key={index}
                          className="flex items-center justify-between bg-gray-100 p-2 rounded-lg text-xs"
                        >
                          <div className="flex-1">
                            <p className="font-medium text-gray-900 truncate">{fileObj.file.name}</p>
                            {fileObj.isUploading && (
                              <div className="relative pt-1">
                                <div className="bg-gray-200 rounded-full h-1 overflow-hidden">
                                  <div
                                    className="bg-indigo-600 h-1 rounded-full transition-all"
                                    style={{ width: `${fileObj.progress}%` }}
                                  />
                                </div>
                                <span className="text-xs text-gray-600 mt-1 block">{fileObj.progress}% Uploading</span>
                              </div>
                            )}
                            {fileObj.isPolling && (
                              <div className="flex items-center gap-1 mt-1">
                                <FontAwesomeIcon icon={faSpinner} className="text-indigo-600 text-xs animate-spin" />
                                <span className="text-xs text-gray-600">Processing...</span>
                              </div>
                            )}
                            {fileObj.error && (
                              <p className="text-xs text-red-600 mt-1">Error: {fileObj.error}</p>
                            )}
                            {fileObj.status && !fileObj.isPolling && (
                              <div className="text-xs mt-1">
                                <p className={fileObj.status === 'completed' ? 'text-green-600' : 'text-red-600'}>
                                  Status: {fileObj.status === 'completed' ? 'Completed' : fileObj.status === 'failed' ? 'Failed' : 'Timeout'}
                                </p>
                                {fileObj.summary && (
                                  <>
                                    <p>Successful Updates: {fileObj.summary.successCount || 0}</p>
                                    <p>Failed Updates: {fileObj.summary.failedCount || 0}</p>
                                    {fileObj.summary.errorMessage && (
                                      <p className="text-red-600">Error: {fileObj.summary.errorMessage}</p>
                                    )}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => handleRemoveFile(fileObj)}
                            className="text-red-500 hover:text-red-700"
                            disabled={fileObj.isUploading || fileObj.isPolling}
                          >
                            <FontAwesomeIcon icon={faTimes} className="h-4 w-4" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="flex justify-end gap-2 mt-3">
                  <button
                    onClick={() => setUploadModalOpen(false)}
                    className="px-3 py-1.5 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 text-xs"
                    disabled={isUploading}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CardPrintingFeeHistory;